import { flowStructureUtil } from '@activepieces/shared';
import { t } from 'i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { TextWithTooltip } from '../../../components/custom/text-with-tooltip';
import { useApRipple } from '../../../components/theme-provider';
import { Button } from '../../../components/ui/button';
import { PieceIcon } from '../../../features/pieces/components/piece-icon';
import { piecesHooks } from '../../../features/pieces/lib/pieces-hook';
import { cn } from '../../../lib/utils';
import { useBuilderStateContext } from '../builder-hooks';

import {
  MentionTreeNode,
  calculatePaddingFromDepth,
  formatValuePreview,
  formatValueTooltip,
  triggerClickOnEnterOrSpace,
} from './data-selector-utils';

type DataSelectorTreeItemProps = {
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  depth: number;
  node: MentionTreeNode;
};

const DataSelectorTreeItem = ({
  node,
  expanded,
  setExpanded,
  depth,
}: DataSelectorTreeItemProps) => {
  const flowVersion = useBuilderStateContext((state) => state.flowVersion);
  const insertMention = useBuilderStateContext((state) => state.insertMention);

  const [ripple, rippleEvent] = useApRipple();

  const isRootStep = depth === 0;
  const step = !node.data.isSlice
    ? flowStructureUtil.getStep(node.data.propertyPath, flowVersion)
    : undefined;
  const stepMetadata = step
    ? piecesHooks.useStepMetadata({ step }).stepMetadata
    : undefined;

  const isExpandable = !!node.children && node.children.length > 0;
  const isInsertable =
    !node.data.isSlice &&
    !(
      node.children &&
      node.children.length > 0 &&
      node.children[0].data.isTestStepNode
    ) &&
    node.data.insertable;

  // Array nodes produce children whose displayName looks like "items [1]".
  const isArrayNode = !!node.children?.[0]?.data?.displayName?.includes('[');
  const showArrayCount = isExpandable && isArrayNode;

  const showValuePreview = !isExpandable && isInsertable;
  const isEmptyValue =
    node.data.value === null ||
    node.data.value === undefined ||
    node.data.value === '';

  const displayNameWithoutIndex = node.data.displayName.replace(
    /^\d+\.\s*/,
    '',
  );

  const handleClick = (e: React.MouseEvent) => {
    if (isExpandable) {
      rippleEvent(e);
      setExpanded(!expanded);
      return;
    }
    if (isInsertable && insertMention) {
      rippleEvent(e);
      insertMention(node.data.propertyPath);
    }
  };

  return (
    <div
      role="button"
      aria-expanded={isExpandable ? expanded : undefined}
      tabIndex={0}
      onKeyDown={triggerClickOnEnterOrSpace}
      ref={ripple}
      onClick={handleClick}
      className={cn(
        'w-full select-none focus:outline-none cursor-pointer group transition-colors',
        'hover:bg-accent focus:bg-accent focus:bg-opacity-75 hover:bg-opacity-75',
      )}
      data-depth={depth}
    >
      <div
        className={cn(
          'flex items-center gap-1.5 pr-2 min-w-0',
          isRootStep ? 'min-h-[40px] py-1.5' : 'min-h-[32px]',
        )}
        style={{ paddingLeft: `${calculatePaddingFromDepth(depth)}px` }}
      >
        {!isRootStep && isExpandable && (
          <ChevronRight
            className={cn(
              'size-3.5 shrink-0 text-muted-foreground transition-transform',
              expanded && 'rotate-90',
            )}
          />
        )}
        {!isRootStep && !isExpandable && (
          <div className="size-3.5 shrink-0" aria-hidden />
        )}

        {isRootStep && stepMetadata && (
          <div className="shrink-0">
            <PieceIcon
              displayName={stepMetadata.displayName}
              logoUrl={stepMetadata.logoUrl}
              showTooltip={false}
              circle={false}
              border={false}
              size="sm"
            ></PieceIcon>
          </div>
        )}

        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <TextWithTooltip tooltipMessage={displayNameWithoutIndex}>
            <span
              className={cn(
                'min-w-0 text-sm text-foreground',
                isRootStep && 'font-medium',
              )}
            >
              {displayNameWithoutIndex}
            </span>
          </TextWithTooltip>

          {showArrayCount && (
            <span className="shrink-0 text-xs text-muted-foreground">
              {t('{count, plural, one {# item} other {# items}}', {
                count: node.children?.length ?? 0,
              })}
            </span>
          )}

          {showValuePreview && (
            <>
              <span className="shrink-0 text-muted-foreground">:</span>
              {isEmptyValue ? (
                <span className="shrink-0 text-sm italic text-muted-foreground/80">
                  {t('empty')}
                </span>
              ) : (
                <TextWithTooltip
                  tooltipMessage={formatValueTooltip(node.data.value)}
                >
                  <span className="min-w-[2ch] flex-1 text-primary text-sm">
                    {formatValuePreview(node.data.value)}
                  </span>
                </TextWithTooltip>
              )}
            </>
          )}
        </div>

        {isInsertable && (
          <Button
            variant="basic"
            size="sm"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              if (insertMention) {
                insertMention(node.data.propertyPath);
              }
            }}
            className={cn(
              'h-6 px-2 text-xs text-primary shrink-0 opacity-0 transition-opacity',
              'group-hover:opacity-100 focus-visible:opacity-100',
            )}
          >
            {t('Insert')}
          </Button>
        )}

        {isRootStep && isExpandable && (
          <ChevronDown
            className={cn(
              'size-4 shrink-0 text-muted-foreground transition-transform',
              !expanded && '-rotate-90',
            )}
          />
        )}
      </div>
    </div>
  );
};

DataSelectorTreeItem.displayName = 'DataSelectorTreeItem';
export { DataSelectorTreeItem };
